import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'; // 👈 useSearchParams & useParams சேர்க்கப்பட்டுள்ளது
import { Search, Eye, Filter, X, Users, Trash2 } from 'lucide-react';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SelectDropdown from '../../customStyles/SelectDropdown';
import ModernTable from '../ui/ModernTable';
import DeleteConfirmationOffcanvas from '../../helpers/DeleteConfirmationOffcanvas';
import PageLoader from '../../helpers/PageLoader';
import CandidateDetail from './CandidateDetail';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';

const CandidateList = () => {
  const { portalName } = useParams();
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // 👈 2. URL Parameters மூலம் வியூக்களைக் கட்டுப்படுத்துகிறோம் [1]
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("candidateView") || "list"; // default is 'list'
  const selectedId = searchParams.get("id"); // view செய்ய வேண்டிய Candidate ID

  // Filters State
  const [searchTerm, setSearchTerm] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Delete State
  const [selectedCandidateToDelete, setSelectedCandidateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchFiltersAndJobs = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllRecruitmentJobs.url);
      if (response.data.success) {
        setJobs(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching jobs filter options:', err);
    }
  }, []);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getAllCandidates.url);
      if (response.data.success) {
        setCandidates(response.data.data || response.data.candidates || []);
      }
    } catch (err) {
      toast.error('Failed to load candidate applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiltersAndJobs();
    fetchCandidates();
  }, [fetchFiltersAndJobs, fetchCandidates]);

  // 👈 3. URL-ன் வியூக்களை மாற்றும் செட்டப் [1]
  const setView = useCallback((newView) => {
    const params = new URLSearchParams(searchParams);
    if (newView === "list") {
      params.delete("candidateView");
      params.delete("id");
    } else {
      params.set("candidateView", newView);
      if (!newView.startsWith("view_")) {
        params.delete("id");
      }
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const filteredCandidates = useMemo(() => {
    return candidates.filter(candidate => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const fullName = `${candidate.firstName} ${candidate.lastName}`.toLowerCase();
        const emailMatch = candidate.email?.toLowerCase().includes(term);
        const appMatch = candidate.applicationId?.toLowerCase().includes(term);
        if (!fullName.includes(term) && !emailMatch && !appMatch) return false;
      }
      if (jobFilter && candidate.jobOpeningId?._id !== jobFilter) return false;
      if (stageFilter && candidate.currentStage !== stageFilter) return false;
      return true;
    });
  }, [candidates, searchTerm, jobFilter, stageFilter]);

  const paginatedData = useMemo(() => {
    if (itemsPerPage === 'All') return filteredCandidates;
    const start = (currentPage - 1) * Number(itemsPerPage);
    return filteredCandidates.slice(start, start + Number(itemsPerPage));
  }, [filteredCandidates, currentPage, itemsPerPage]);

  const handleDeleteConfirm = async () => {
    if (!selectedCandidateToDelete) return;
    setIsDeleting(true);
    try {
      const response = await api.delete(SummaryApi.deleteCandidate.url(selectedCandidateToDelete._id));
      if (response.data.success) {
        toast.success('Candidate profile deleted successfully');
        setSelectedCandidateToDelete(null);
        fetchCandidates();
      }
    } catch (err) {
      toast.error('Deletion failed or unauthorized');
    } finally {
      setIsDeleting(false);
    }
  };

  // 👈 4. வியூ கிளிக் செய்யும்போது URL Parameter-ஐ அப்டேட் செய்கிறோம் [1]
  const handleView = (row) => {
    const params = new URLSearchParams(searchParams);
    params.set("candidateView", "view_candidate");
    params.set("id", row._id);
    setSearchParams(params, { replace: true });
  };

  const getStageBadge = (stage) => {
    const styles = {
      applied: 'bg-blue-100 text-blue-700 border-blue-200',
      resume_screening: 'bg-purple-100 text-purple-700 border-purple-200',
      shortlisted: 'bg-pink-100 text-pink-700 border-pink-200',
      interview_scheduled: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      interview_completed: 'bg-orange-100 text-orange-700 border-orange-200',
      selected: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      offered: 'bg-amber-100 text-amber-700 border-amber-200',
      onboarding: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      hired: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
      on_hold: 'bg-gray-100 text-gray-700 border-gray-200',
    };
    return (
      <span className={`px-2.5 py-1 text-xs font-bold rounded-full border uppercase ${styles[stage] || 'bg-gray-50 text-gray-700'}`}>
        {stage?.replace('_', ' ')}
      </span>
    );
  };

  const columns = [
    {
      label: 'Candidate',
      key: 'name',
      render: (row) => (
        <div className="flex flex-col min-w-[180px]">
          <span className="font-semibold text-gray-900 text-sm">{row.firstName} {row.lastName}</span>
          <span className="text-[11px] text-gray-400 mt-0.5">{row.applicationId || 'No ID'}</span>
        </div>
      )
    },
    {
      label: 'Contact Info',
      key: 'email',
      render: (row) => (
        <div className="text-xs space-y-0.5">
          <div className="text-gray-800">{row.email}</div>
          <div className="text-gray-400">{row.mobile || 'No Mobile'}</div>
        </div>
      )
    },
    {
      label: 'Job Applied For',
      key: 'jobOpeningId',
      render: (row) => (
        <div className="text-xs">
          <div className="font-medium text-gray-800">{row.jobOpeningId?.title || 'General / Unknown'}</div>
          <div className="text-gray-400 text-[11px] mt-0.5">{row.jobOpeningId?.jobId || '-'}</div>
        </div>
      )
    },
    {
      label: 'Experience',
      key: 'totalExperienceYears',
      render: (row) => <span className="text-xs font-medium text-gray-700">{row.totalExperienceYears || 0} Years</span>
    },
    {
      label: 'Recruitment Stage',
      key: 'currentStage',
      render: (row) => getStageBadge(row.currentStage)
    }
  ];

  const tableActions = [
    {
      label: 'View Full Profile',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row) => handleView(row) // 👈 மாற்றியமைக்கப்பட்டுள்ளது
    },
    {
      label: 'Delete Profile',
      icon: <Trash2 className="h-4 w-4 text-red-600" />,
      onClick: (row) => setSelectedCandidateToDelete(row)
    }
  ];

  // 👈 5. வியூ கண்டிஷன் செக் (Render checks for sub-views) [1]
  if (view === "view_candidate") {
    return (
      <CandidateDetail 
        id={selectedId} 
        onClose={() => setView("list")} 
      />
    );
  }

  if (loading) return <PageLoader />;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          {/* 👈 மெயின் பட்டியலின் முகப்பில் இருக்கும்போது பேக் பட்டன் மெயின் டேபிற்கு அழைத்துச் செல்லும் */}
          <button 
            onClick={() => navigate(`/${portalName}/hr/recruitment-management?tab=candidates`)} 
            className="rounded-lg bg-red-50 border border-red-100 text-textPrimary hover:text-primary p-2 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Candidates Directory</h1>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ID, Name or Email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition-colors text-sm ${showFilters ? 'bg-red-50 border-red-300 text-red-700 font-semibold' : 'bg-white border-gray-300'}`}>
            <Filter className="h-5 w-5" /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="bg-white p-4 rounded-xl shadow-sm border grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
            <SelectDropdown label="Filter by Job Post" options={[{ title: 'All Jobs', _id: '' }, ...jobs]} value={jobFilter} onChange={setJobFilter} valueKey="_id" labelKey="title" searchable />
            <SelectDropdown
              label="Filter by Recruitment Stage"
              options={[
                { label: 'All Stages', value: '' },
                { label: 'Applied', value: 'applied' },
                { label: 'Screening', value: 'screening' },
                { label: 'Interview Scheduled', value: 'interview_scheduled' },
                { label: 'Interviewed', value: 'interviewed' },
                { label: 'Shortlisted', value: 'shortlisted' },
                { label: 'Offered', value: 'offered' },
                { label: 'Hired', value: 'hired' },
                { label: 'Rejected', value: 'rejected' }
              ]}
              value={stageFilter}
              onChange={setStageFilter}
              valueKey="value"
              labelKey="label"
            />
            <button onClick={() => { setJobFilter(''); setStageFilter(''); setSearchTerm(''); }} className="text-red-600 text-sm font-medium flex items-center gap-1 hover:underline col-span-1 sm:col-span-2 pt-2">
              <X className="h-4 w-4" /> Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Modern Table */}
      {filteredCandidates.length > 0 && (
        <ModernTable
          columns={columns}
          data={paginatedData}
          actions={tableActions}
          selectable={false}
          pagination={{
            totalItems: filteredCandidates.length,
            itemsPerPage: itemsPerPage,
            currentPage: currentPage,
            onPageChange: setCurrentPage,
            onItemsPerPageChange: (v) => { setItemsPerPage(v); setCurrentPage(1); }
          }}
        />
      )}

      {/* Empty State */}
      {!loading && filteredCandidates.length === 0 && (
        <div className="bg-white rounded-xl border p-12 text-center mt-4">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No Candidates found</h3>
          <p className="text-gray-500">Try adjusting search parameters or filter configs.</p>
        </div>
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmationOffcanvas
        isOpen={!!selectedCandidateToDelete}
        onClose={() => setSelectedCandidateToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Candidate Record"
        itemName={`${selectedCandidateToDelete?.firstName} ${selectedCandidateToDelete?.lastName}`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default CandidateList;